import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MESSAGE_ONCLICK_SUFFIX, MESSAGE_ONCLICK_INNER_PREFIX, MESSAGE_ONCLICK_INNER_SUFFIX } from '../../utils/consts';
import { formatCell } from '../../utils/numbers';
import css from './TableCell.css';
import Icon, { IconSizes, IconTypes } from '../foundations/Icon';
import Tooltip from '../molecules/Tooltip';
import CellLoader from './CellLoader';

const createClickableTag = (clickableText, onClick) => <a onClick={onClick}> {clickableText} </a>;

export const getClickableText = (cellValue, onTextClick, row) => {
    const clickableActionsRegex = new RegExp(`(.+?${MESSAGE_ONCLICK_SUFFIX})`, 'g');
    const clickableActionsGroups = cellValue.match(clickableActionsRegex);

    return (
        clickableActionsGroups &&
        clickableActionsGroups.map(groupValue => {
            const clickablePropsRegex = new RegExp(
                `(.*)${MESSAGE_ONCLICK_INNER_PREFIX}(.+)${MESSAGE_ONCLICK_INNER_SUFFIX}(.+)${MESSAGE_ONCLICK_SUFFIX}(.*)`
            );
            const result = clickablePropsRegex.exec(groupValue);
            if (onTextClick && result) {
                const preOnClickText = result[1];
                const action = result[2];
                const clickableText = result[3];
                const postOnClickText = result[4];
                return (
                    <span key={groupValue}>
                        {preOnClickText}
                        {createClickableTag(clickableText, () => onTextClick(action, row))}
                        {postOnClickText}
                    </span>
                );
            }
            return null;
        })
    );
};

const TableCell = ({
    className,
    containerStyle,
    color,
    value,
    tdStyle = {},
    format = null,
    onTextClick = null,
    row,
    rightCellElement = null,
    colSpan,
    tooltipText,
    disabled = false,
    isLoading,
    containerClassName = '',
    dataTestId = '',
    loaderClass,
    textClassName = '',
}) => {
    const style = {
        padding: '5px 0',
        display: 'inline-block',
        float: format && ['number', 'currency', 'percentage'].includes(format.type) ? 'right' : null,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
    };
    const clickableCellText = value && getClickableText(value, onTextClick, row);
    return (
        <td
            className={className}
            style={{
                ...containerStyle,
                ...tdStyle,
                color,
            }}
            colSpan={colSpan}
            data-testid={dataTestId}
        >
            <div
                className={classNames(containerClassName, {
                    [css.tableCellWithRightElement]: rightCellElement,
                    [css.disabled]: disabled,
                })}
            >
                {isLoading ? (
                    <CellLoader loaderClass={loaderClass} />
                ) : (
                    <>
                        {clickableCellText ? (
                            <span className={textClassName} style={style}> {clickableCellText} </span>
                        ) : (
                            <span
                                className={textClassName}
                                style={style}
                                dangerouslySetInnerHTML={{ __html: formatCell(value, format, onTextClick) }}
                            />
                        )}
                        {rightCellElement ? (
                            <div className={css.rightAreaSection}>
                                {rightCellElement.icon}
                                <span className={css.rightAreaText}>{rightCellElement.text}</span>
                            </div>
                        ) : null}
                        {tooltipText && (
                            <Tooltip
                                {...(row[tooltipText]
                                    ? { title: row[tooltipText] }
                                    : { titleTranslationKey: tooltipText })}
                            >
                                <Icon
                                    type={IconTypes.MINGCUTE}
                                    name="question-line"
                                    size={IconSizes.X_SMALL}
                                    className={css.tooltipIcon}
                                />
                            </Tooltip>
                        )}
                    </>
                )}
            </div>
        </td>
    );
};

TableCell.propTypes = {
    disabled: PropTypes.bool,
    onTextClick: PropTypes.func,
    rightCellElement: PropTypes.objectOf(
        PropTypes.shape({
            text: PropTypes.string,
            icon: PropTypes.element,
        })
    ),
    containerClassName: PropTypes.string,
    dataTestId: PropTypes.string,
    loaderClass: PropTypes.string,
    textClassName: PropTypes.string,
};

export default TableCell;
